import { createContext, useEffect, useRef, useState } from 'react'

import { withAuthStore } from 'context/AuthStore'
import { FILTER_INIT } from 'constants/initialStates'
import { useResErrors, useStateCallback } from 'hooks'
import { deepCopyObject } from 'lib/utils'
import { AuthService } from 'lib/api'

export const HomeStoreContext = createContext({
  homeStore: {},
  homeStoreFunc: {}
})

const HomeStoreProvider = props => {
  const _isMounted = useRef(true)
  const { standardCheck } = useResErrors()
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useStateCallback(FILTER_INIT)
  const [pagination, setPagination] = useStateCallback({
    pageCount: 0,
    page: 0,
    rowsPerPage: 10
  })
  const [dictionary, setDictionary] = useStateCallback({
    branches: [],
    projects: [],
    subdivisions: [],
    roles: [],
    creditProducts: []
  })
  const [users, setUsers] = useStateCallback([])

  useEffect(() => {
    getInitialData()

    return () => {
      _isMounted.current = false
    }
  }, [])

  const onFilterChange = (key, value) =>
    setFilter(
      prevState => ({
        ...prevState,
        [key]: value,
        ...(key === 'projectCode' ? { roleCode: null } : {})
      }),
      updated => {
        key === 'projectCode' && onGetRoles(updated.projectCode?.code)
      }
    )

  const onFilterReset = () =>
    setFilter(deepCopyObject(FILTER_INIT), updated => {
      setPagination(prevState => ({ ...prevState, page: 0 }))
      onSearch(updated, { page: 0, rowsPerPage: pagination.rowsPerPage })()
    })

  const onPaginationChange = page =>
    setPagination(
      prevState => ({ ...prevState, page }),
      updated => {
        onSearch(filter, updated)()
      }
    )

  const changeRowsPerPageByView = (isMob = false) =>
    setPagination(
      prevState => ({
        ...prevState,
        rowsPerPage: isMob ? 3 : 10
      }),
      async updated => {
        await onSearch(filter, updated)()
      }
    )

  const onSearch =
    (data = filter, pages = pagination) =>
    async () => {
      const {
        username,
        userFullname,
        roleCode,
        subdivision,
        projectCode,
        branchCode,
        isActive,
        isSystemUser
      } = data

      const { page, rowsPerPage } = pages

      setLoading(true)

      const filter = {
        username: username || undefined,
        userFullname: userFullname || undefined,
        roleCode: roleCode?.code || undefined,
        subdivision: subdivision?.code || undefined,
        projectCode: projectCode?.code || undefined,
        userBranchCode: branchCode?.code || undefined,
        isActive: isActive !== 'all' ? isActive : undefined,
        isSystemUser: !isSystemUser ? undefined : isSystemUser,
        offset: page * rowsPerPage,
        limit: rowsPerPage
      }

      await AuthService.getUsers(filter).then(res =>
        standardCheck(
          res,
          () => {
            setLoading(false)
            setUsers(res.data.userList || [])
            setPagination(prevState => ({
              ...prevState,
              pageCount: Math.ceil(res.data?.countFiltered / rowsPerPage) || 0
            }))
          },
          {
            onFail: () => {
              setLoading(false)
            }
          }
        )
      )
    }

  const onGetProjects = async () => {
    await AuthService.getActiveProjects().then(res =>
      standardCheck(res, () => {
        setDictionary(prevState => ({
          ...prevState,
          projects: res.data || []
        }))
      })
    )
  }

  const onGetProducts = async () => {
    await AuthService.getCreditProducts().then(res =>
      standardCheck(res, () => {
        setDictionary(prevState => ({
          ...prevState,
          creditProducts: res.data.list || []
        }))
      })
    )
  }

  const onGetBranches = async () => {
    await AuthService.getBranches().then(response =>
      standardCheck(response, () => {
        setDictionary(prevState => ({
          ...prevState,
          branches: response.data || []
        }))
      })
    )
  }

  const onGetSubdivisions = async () => {
    await AuthService.getSubdivisions().then(response =>
      standardCheck(response, () => {
        const sortedSubdivisions = (response.data || []).sort((a, b) =>
          a.name.localeCompare(b.name, 'ru')
        )
        setDictionary(prevState => ({
          ...prevState,
          subdivisions: sortedSubdivisions
        }))
      })
    )
  }

  const onGetRoles = async project => {
    setLoading(true)
    await AuthService.getRolesProject(project).then(res =>
      standardCheck(
        res,
        () => {
          setLoading(false)
          setDictionary(prevState => ({
            ...prevState,
            roles: res.data || []
          }))
        },
        {
          onFail: () => setLoading(false)
        }
      )
    )
  }

  const setLoadingState = state => {
    setLoading(state)
  }

  //find user by id and update field isActive
  const onLockUnlockUser = (id, isActive) => {
    const user = users.find(user => user.id === id)
    user.isActive = isActive
    setUsers([...users])
  }

  const getInitialData = async () => {
    setLoading(true)

    await onGetProjects()
    await onGetBranches()
    await onGetSubdivisions()
    await onGetProducts()
    onSearch()

    setLoading(false)
  }

  const homeStore = {
    loading,
    filter,
    pagination,
    dictionary,
    users
  }

  const homeStoreFunc = {
    onFilterChange,
    onFilterReset,
    changeRowsPerPageByView,
    onSearch: onSearch(filter),
    onPaginationChange,
    onLockUnlockUser,
    setLoadingState
  }

  return (
    <HomeStoreContext.Provider value={{ homeStore, homeStoreFunc }}>
      {props.children}
    </HomeStoreContext.Provider>
  )
}

export default withAuthStore(HomeStoreProvider)
